'use client';

// Fix build error for React PDF
import 'core-js/proposals/promise-with-resolvers';

import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

import * as React from 'react';
const { useRef, useState, useEffect, useCallback } = React;

import { Stage, Layer, Image as KonvaImage, Rect, Group } from 'react-konva';
import type { Stage as StageType } from 'konva/lib/Stage';
import type { Layer as LayerType } from 'konva/lib/Layer';
import type { Image as ImageType } from 'konva/lib/shapes/Image';

import useImage from 'use-image';

import usePreventPinchZoomIOS from './hooks/usePreventPinchZoomIOS';
import useStageSize from './hooks/useStageSize';
import useRenderDetections from './hooks/useRenderDetections';
import useShortcuts from '../../hooks/useShortcuts';

import { getDocumentType, renderImageDoc } from '@/utils/utils';

import CanvasTooltipMRZ from './components/tooltips/CanvasTooltipMRZ';
import CanvasDetectionControls from './components/controls/CanvasDetectionControls';
import CanvasActionControls from './components/controls/CanvasActionControls';
import CanvasRotationControls from './components/common/CanvasRotationControls';
import CanvasPaginationControls from './components/controls/CanvasPaginationControls';
import type { CanvasProps } from '../../types/canvas.types';
import { useCanvasState } from './hooks/useCanvasState';
import { useCanvasInteractions } from './hooks/useCanvasInteractions';
import { useCanvasDocument } from './hooks/useCanvasDocument';
import { Loader2 } from 'lucide-react';
// @ts-expect-error This does not exist outside of polyfill which this is doing
if (typeof Promise.withResolvers === 'undefined') {
  if (window)
    // @ts-expect-error This does not exist outside of polyfill which this is doing
    window.Promise.withResolvers = function () {
      let resolve, reject;
      const promise = new Promise((res, rej) => {
        resolve = res;
        reject = rej;
      });
      return { promise, resolve, reject };
    };
}

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const Canvas = ({
  resultData,
  imageData,
  downloadData,
  anomalyData,
  pageIndex,
  setPageIndex,
  toggle,
  handleToggleChange,
  setIsPopupOpen,
  setIsNotesModalOpen,
  hoverDetection,
  setHoverDetection,
  hideDetectionCanvas,
  setHideDetectionCanvas,
  selected2DDocId,
  selectedMrzId,
  selectedMrz,
  notesData,
  isImageLoading,
}: CanvasProps) => {
  const [documentType, setDocumentType] = useState(
    getDocumentType(imageData?.image || '', resultData?.nom || '')
  );

  useEffect(() => {
    const type = getDocumentType(imageData?.image || '', resultData?.nom || '');
    if (type !== documentType) {
      setDocumentType(type);
    }
  }, [documentType, imageData, resultData?.nom]);

  const stageRef = useRef<StageType>(null);
  const layerRef = useRef<LayerType>(null);
  const konvaImageRef = useRef<ImageType>(null);

  const handleImageRef = useCallback((node: ImageType | null) => {
    if (node && konvaImageRef.current !== node) {
      // On n'assigne QUE si le nœud est différent pour stopper la boucle
      (konvaImageRef as any).current = node;
    }
  }, []);

  const [IMGDoc, IMGDocStatus] = useImage(
    renderImageDoc(toggle, imageData, anomalyData) || ''
  );

  const {
    scale,
    setScale,
    position,
    setPosition,
    positionPDF,
    setPositionPDF,
    offset,
    setOffset,
    documentRotation,
    isDocumentLoaded,
    setIsDocumentLoaded,
    setIsPointerInStage,
    pageHeight,
    setPageHeight,
    pageWidth,
    setPageWidth,
    documentRatio,
    setDocumentRatio,
    handleRotate,
  } = useCanvasState();

  const { handleMouseEnter, handleMouseLeave, handleDragMove, handleWheel } =
    useCanvasInteractions(
      documentType,
      scale,
      setScale,
      setPosition,
      setPositionPDF,
      offset
    );

  const { numPages, onDocumentLoadSuccess, onPageLoadSuccess } =
    useCanvasDocument({
      documentType,
      imageData,
      pageIndex,
      stageRef,
      setOffset,
      setPosition,
      setPositionPDF,
      setPageHeight,
      setPageWidth,
      setDocumentRatio,
      setIsDocumentLoaded,
      IMGDocStatus,
      IMGDoc,
      konvaImageRef,
    });

  usePreventPinchZoomIOS();

  const stageSize = useStageSize();

  const renderedDetectionsCanvas = useRenderDetections({
    resultData,
    pageIndex,
    documentRatio,
    offset,
    hideDetectionCanvas,
    hoverDetection,
    setHoverDetection,
    handleToggleChange,
    selected2DDocId,
    selectedMrzId,
  });

  useEffect(() => {
    setScale(1);
  }, [pageIndex, setScale]);

  // Custom shortcuts only enabled in canvas
  useShortcuts({
    resultData,
    setHideDetectionCanvas,
    setPageIndex,
    numPages,
    pageIndex,
    handleRotate,
    toggle,
  });

  if (!resultData || isImageLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-neutralText-dark" />
      </div>
    );
  }

  return (
    <div className="relative h-full w-full">
      <Stage
        className="relative z-20 overflow-hidden"
        ref={stageRef}
        onMouseEnter={() => setIsPointerInStage(true)}
        onMouseLeave={() => setIsPointerInStage(false)}
        width={stageSize.width}
        height={stageSize.height}
      >
        <Layer
          ref={layerRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onDragMove={handleDragMove}
          onWheel={handleWheel}
          scaleX={scale}
          scaleY={scale}
          x={position.x}
          y={position.y}
          draggable={true}
          rotation={documentRotation}
        >
          {pageWidth > 0 && pageHeight > 0 && (
            <Rect
              width={pageWidth}
              height={pageHeight}
              x={0}
              y={0}
              offsetY={offset.y}
              offsetX={offset.x}
              opacity={0.5}
            />
          )}
          
          <KonvaImage 
            ref={handleImageRef}
            image={IMGDoc} 
          />
          {toggle === 'preview' &&
            isDocumentLoaded &&
            Array.isArray(renderedDetectionsCanvas) && (
              <Group>
                {renderedDetectionsCanvas.map((detection, index) => (
                  <React.Fragment key={`detection-${index}`}>
                    {detection}
                  </React.Fragment>
                ))}
              </Group>
            )}

          {toggle === '2ddoc' &&
            isDocumentLoaded &&
            Array.isArray(renderedDetectionsCanvas) && (
              <Group>
                {renderedDetectionsCanvas.map((detection, index) => {
                  if (detection.props.type !== '2ddoc') {
                    return null;
                  }

                  return (
                    <React.Fragment key={`detection-${index}`}>
                      <div className="ddoc-container">{detection}</div>
                    </React.Fragment>
                  );
                })}
              </Group>
            )}

          {toggle === 'mrz' &&
            !!isDocumentLoaded &&
            Array.isArray(renderedDetectionsCanvas) && (
              <Group>
                {renderedDetectionsCanvas.map((detection, index) => {
                  if (detection.props.type !== 'mrz') {
                    return null;
                  }

                  return (
                    <React.Fragment key={`detection-${index}`}>
                      {detection}
                    </React.Fragment>
                  );
                })}
              </Group>
            )}
        </Layer>

        {toggle === 'mrz' && selectedMrz && (
          <Layer x={14} y={14}>
            <CanvasTooltipMRZ
              handleToggleChange={handleToggleChange}
              rows={selectedMrz}
            />
          </Layer>
        )}

        <Layer>
          {toggle === 'preview' && (
            <Group>
              <CanvasDetectionControls
                hideDetectionCanvas={hideDetectionCanvas}
                setHideDetectionCanvas={setHideDetectionCanvas}
              />
              <CanvasActionControls
                stageSize={stageSize}
                resultData={resultData}
                downloadData={downloadData}
                setIsPopupOpen={setIsPopupOpen}
                setIsNotesModalOpen={setIsNotesModalOpen}
                notesData={notesData}
              />
            </Group>
          )}

          <CanvasRotationControls
            stageSize={stageSize}
            handleRotate={handleRotate}
          />

          <CanvasPaginationControls
            stageSize={stageSize}
            numPages={numPages}
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            documentType={documentType}
          />
        </Layer>
      </Stage>
      <div
        className={`absolute top-0 h-full w-full overflow-hidden ${
          isDocumentLoaded ? '' : 'hidden'
        }`}
      >
        {documentType === 'pdf' && (
          <div
            style={{
              transform: `translate(${positionPDF.x}px, ${positionPDF.y}px) rotate(${documentRotation}deg)`,
            }}
            className="absolute top-0"
          >
            <Document
              file={`data:image/;base64,${imageData?.image}`}
              onLoadSuccess={onDocumentLoadSuccess}
              className={`select-none`}
            >
              <Page
                onLoadSuccess={onPageLoadSuccess}
                renderAnnotationLayer={false}
                pageIndex={pageIndex}
                height={pageHeight}
                width={pageWidth}
                scale={scale}
              />
            </Document>
          </div>
        )}
      </div>
    </div>
  );
};

export default Canvas;
